/**
 * Generates HTML email template for "Advance Invoice Payment Confirmed".
 * Sent to clients after an advance or additional services invoice payment is received.
 * Uses shared layout from emailLayout.js for header/footer.
 */

import { spacer, emailDocOpen, emailDocClose, emailHeader, emailFooter, icon } from '../emailLayout';

const fmt = (n) => Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

/**
 * @param {Object}  opts
 * @param {string}  opts.clientName
 * @param {number}  opts.totalDue        - subtotal (sum of all service amounts, incl. per-service tax)
 **@param {number}  opts.totalTax        - sum of taxes
 * @param {number}  opts.totalAmount     - grand total with card fee
 * @param {string}  [opts.quoteNumber]
 * @param {Array}   [opts.services]      - [{ item, amount, tax }]
 * @param {boolean} [opts.oneOff]        - when true, uses "advance invoice" messaging
 * @param {string}  [opts.cardBrand]     - card brand e.g. "Visa"
 * @param {string}  [opts.cardLast4]     - last 4 digits of card
 */
export const buildAdvanceInvoicePaymentConfirmedEmailHtml = ({
  clientName,
  totalDue,
  totalTax,
  totalAmount,
  quoteNumber,
  services,
  oneOff = true,
  cardBrand,
  cardLast4,
}) => {

  const cardFee = fmt(totalDue * 0.04);
  const subTotal = fmt(totalDue - totalTax);

  // ── Per-service rows ─────────────────────────────────────────────────────
  const serviceRowsHtml = (() => {
    if (services?.length > 0) {
      return services.map((s, i) => {
        const isLast = i === services.length - 1;
        return `
              <tr>
                <td colspan="2" style="padding:0 20px ${isLast ? '0' : '6px'} 20px;">
                  <table role="presentation" width="100%" cellpadding="0" cellspacing="0">
                    <tr>
                      <td style="padding:12px 20px 15px;font-size:12px;font-weight:700;color:#111827;background-color:#F7F7F7;">${s.item || s.name || ''}</td>
                      <td align="right" style="padding: 12px 20px 12px;font-size:14px;font-weight:700;color:#111827;background-color:#F7F7F7;">$${fmt(s.amount)}</td>
                    </tr>
                    ${oneOff ? `<tr>
                      <td style="padding:0px 20px 12px 28px;font-size:13px;color:#6B7280;background-color:#F7F7F7;">Service Cost</td>
                      <td align="right" style="padding:0px 20px 12px;font-size:13px;color:#6B7280;background-color:#F7F7F7;">$${fmt(s.amount)}</td>
                    </tr>` : ''}
                  </table>
                </td>
              </tr>`;
      }).join('');
    }
    return '';
  })();

  // ── Card brand badge ──────────────────────────────────────────────────────
  const cardBrandBadge = cardBrand?.toLowerCase() === 'visa'
    ? `<span style="display:inline-block;background-color:#1A1F71;color:#FFFFFF;font-size:9px;font-weight:900;padding:2px 5px;border-radius:3px;letter-spacing:0.5px;font-style:italic;vertical-align:middle;margin-right:6px;">VISA</span>`
    : cardBrand
      ? `<span style="display:inline-block;background-color:#252525;color:#FFFFFF;font-size:9px;font-weight:700;padding:2px 5px;border-radius:3px;vertical-align:middle;margin-right:6px;">${cardBrand.toUpperCase()}</span>`
      : '';

  return `
${emailDocOpen({ title: 'Payment Confirmation' })}
${emailHeader({ title: 'Booking Secured' })}

        ${spacer()}

        <!-- ===== CLIENT / REFERENCE ROW ===== -->
        <table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="max-width:600px;background-color:#FFFFFF;overflow:hidden;border:1px solid #EBEBEB;">
          <tr>
            <td style="padding:16px 24px;">
              <table role="presentation" width="100%" cellpadding="0" cellspacing="0">
                <tr>
                  <td> 
                    <span style="font-size:10px;text-transform:uppercase;letter-spacing:0.7px;color:#9CA3AF;font-weight:600;">Client</span><br/>
                    <span style="font-size:16px;font-weight:700;color:#111827;">${clientName || 'Valued Client'}</span>
                  </td>
                  ${quoteNumber ? `<td align="right">
                    <span style="font-size:10px;text-transform:uppercase;letter-spacing:0.7px;color:#9CA3AF;font-weight:600;">TR Number</span><br/>
                    <span style="font-size:16px;font-weight:700;color:#111827;">${quoteNumber}</span>
                  </td>` : ''}
                </tr>
              </table>
            </td>
          </tr>
        </table>

        ${spacer()}

        <!-- ===== NOTIFICATION CARD ===== -->
        <table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="max-width:600px;background-color:#FFFFFF;overflow:hidden;border:1px solid #EBEBEB;">
          <tr>
            <td style="padding:28px 28px 32px;">
              <!-- Icon -->
              <table role="presentation" cellpadding="0" cellspacing="0" style="margin-bottom:20px;">
                <tr>
                  <td style="width:48px;height:48px;text-align:center;vertical-align:middle;">
                    <div style="width:48px;height:48px;background-color:#DCFCE7;border-radius:50%;text-align:center;line-height:48px;">
                    ${icon('check', 24, '#16A34A')}
                    </div>
                  </td>
                </tr>
              </table>
              <!-- Body text -->
              <p style="margin:0;font-size:14px;color:#374151;line-height:1.7;">
                ${oneOff ? 'Your payment has been received. Please review the invoice details below.' : `Your additional invoice for <strong style="color:#111827;">${clientName}</strong> under Trip ID <strong style="color:#111827;">${quoteNumber}</strong> has been paid for. Kindly review the invoice details below.`}
              </p>
            </td>
          </tr>
        </table>

        ${spacer()}

        <!-- ===== INVOICE DETAILS ===== -->
        <table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="max-width:600px;background-color:#FFFFFF;overflow:hidden;border:1px solid #EBEBEB;">
          <tr>
            <td colspan="2" style="padding:14px 20px 8px;">
              <span style="font-size:10px;text-transform:uppercase;letter-spacing:0.7px;color:#9CA3AF;font-weight:600;">Invoice Details</span>
            </td>
          </tr>
          ${serviceRowsHtml}
          <tr><td colspan="2" style="padding:8px 0 14px 0;"></td></tr>
        </table>

        ${spacer()}

        <!-- ===== PRICE BREAKDOWN ===== -->
        <table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="max-width:600px;background-color:#FFFFFF;overflow:hidden;border:1px solid #EBEBEB;">
          <tr>
            <td colspan="2" style="padding:14px 20px 8px;">
              <span style="font-size:10px;text-transform:uppercase;letter-spacing:0.7px;color:#9CA3AF;font-weight:600;">Price Breakdown</span>
            </td>
          </tr>

          <tr>
            <td style="padding:6px 20px 4px;font-size:13px;color:#6B7280;">Subtotal Cost</td>
            <td align="right" style="padding:6px 20px 4px;font-size:13px;color:#6B7280;">$${subTotal}</td>
          </tr>
          <tr>
            <td style="padding:4px 20px 10px;font-size:13px;color:#6B7280;">Taxes(10%)</td>
            <td align="right" style="padding:4px 20px 10px;font-size:13px;color:#6B7280;">$${fmt(totalTax)}</td>
          </tr>

          <tr>
            <td colspan="2" style="padding:0 20px;"><div style="border-top:1px solid #E5E7EB;"></div></td>
          </tr>

          <tr>
            <td style="padding:14px 20px ${cardFee ? '4px' : '14px'};font-size:15px;font-weight:700;color:#111827;">Total Due</td>
            <td align="right" style="padding:14px 20px ${cardFee ? '4px' : '14px'};font-size:18px;font-weight:700;color:#111827;">$${totalAmount}</td>
          </tr>

          ${cardFee ? `<tr>
            <td style="padding:4px 20px 14px;font-size:13px;color:#6B7280;">Credit/Debit Card Fee(4%)</td>
            <td align="right" style="padding:4px 20px 14px;font-size:13px;color:#6B7280;">$${fmt(cardFee)}</td>
          </tr>` : ''}

          ${cardLast4 ? `<tr>
            <td colspan="2" style="padding:0 20px;"><div style="border-top:1px solid #E5E7EB;"></div></td>
          </tr>
          <tr>
            <td colspan="2" style="padding:10px 20px 4px;">
              <span style="font-size:10px;text-transform:uppercase;letter-spacing:0.7px;color:#9CA3AF;font-weight:600;">Paid By</span>
            </td>
          </tr>
          <tr>
            <td style="padding:4px 20px 14px;font-size:14px;font-weight:700;color:#111827;">${clientName || ''}</td>
            ${cardLast4 ? `<td align="right" style="padding:4px 20px 14px;font-size:13px;color:#6B7280;white-space:nowrap;">${cardBrandBadge}•••• •••• •••• ••••</td>` : '<td></td>'}
          </tr>` : ''}
        </table>

        ${spacer()}

${emailFooter()}
${emailDocClose()}`;
};
